import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import type { FunctionComponent, ReactNode } from 'react';

import type { PanelNames } from '@trello/split-screen';
import {
  useSplitScreenResize,
  useSplitScreenSharedState,
} from '@trello/split-screen';
import { getTestId } from '@trello/test-ids';
import type { SplitScreenTestIds } from '@trello/test-ids/';

import { CollapsibleSplitScreenResizeDivider } from './CollapsibleSplitScreenResizeDivider';

import styles from './CollapsibleSplitScreenPanel.less';

interface CollapsibleSplitScreenPanelProps {
  children?: ReactNode;
  panelName: PanelNames;
  isHidden?: boolean;
}

export const CollapsibleSplitScreenPanel: FunctionComponent<
  CollapsibleSplitScreenPanelProps
> = ({ children, isHidden, panelName }) => {
  const { isRightmostPanel, panelRef, onPanelLoad, previousPanelWidth } =
    useSplitScreenResize({
      panelName,
    });

  const { panels, areMultiplePanelsOpen } = useSplitScreenSharedState();

  const storedPanelWidth = panels.panelWidths[panelName];

  const [isCollapsed, setIsCollapsed] = useState(isHidden);
  const [panelWidth, setPanelWidth] = useState(storedPanelWidth.width);
  //we need to disable transitions when we are loading and when the user is dragging the dividers
  const [enableTransitions, setEnableTransitions] = useState(false);

  const panelFlexValue = useMemo(() => {
    if (isHidden) {
      return '0 0 0px';
    }

    if (!areMultiplePanelsOpen || isRightmostPanel) {
      return `1 1 ${panelWidth}px`;
    }

    if (!isRightmostPanel) {
      return `0 0 ${panelWidth}px`;
    }

    return '0 0 0px';
  }, [isHidden, areMultiplePanelsOpen, isRightmostPanel, panelWidth]);

  useEffect(() => {
    setEnableTransitions(true);
    onPanelLoad();

    const isPanelCollapsed = !panels[panelName];
    setIsCollapsed(isPanelCollapsed);

    if (!isPanelCollapsed) {
      setPanelWidth(previousPanelWidth);
    }
    // I just want this to run on render once - this is setup only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={cx(styles.panel, styles[panelName], {
          [styles.singlePanel]: !areMultiplePanelsOpen,
          [styles.panelTransitions]: enableTransitions,
          [styles.rightmostPanel]: isRightmostPanel,
          [styles.hidden]: isHidden,
          [styles.collapsed]: isCollapsed,
        })}
        data-testid={getTestId<SplitScreenTestIds>('split-screen-panel')}
        ref={panelRef}
        style={{
          flex: panelFlexValue,
        }}
        aria-hidden={isHidden}
      >
        {children}
      </div>
      <CollapsibleSplitScreenResizeDivider
        enableTransitionsCallback={setEnableTransitions}
        isCollapsedCallback={setIsCollapsed}
        isPanelHidden={isHidden ?? true}
        panelName={panelName}
        panelWidthCallback={setPanelWidth}
      />
    </>
  );
};
